<template>
  <div
    class="popup"
    :style="{ left: `${x}px`, top: `${y}px` }"
    :class="`popup--${position}`"
    ref="popup"
  >
    <div class="popup__tools">
      <VSelect
        v-if="items && Array.isArray(label)"
        class="select select--default"
        :options="items"
        :reduce="(item) => item.id"
        :getOptionLabel="(item) => `${item[label[0]]} ${item[label[1]]}`"
        :placeholder="selected"
        v-model="value"
        ref="vselect"
      />
      <VSelect
        v-else-if="items"
        class="select select--default"
        :options="items"
        :reduce="(item) => item.id"
        :label="label"
        :placeholder="selected"
        v-model="value"
        ref="vselect"
      />
      <Datepicker
        v-else-if="isDate"
        class="input input--default"
        ref="datepicker"
        :config="statics.datePickerConfig"
        v-model="value"
      />
      <input
          v-else-if="isPrice"
          type="number"
          min="0"
          step=".01"
          class="input input--default"
          :value="selected"
          @input="value = $event.target.value"
          ref="priceInput"
      />
      <input
        v-else
        type="text"
        class="input input--default"
        :value="selected"
        @input="value = $event.target.value"
        ref="textInput"
      />
      <div class="popup__button popup__button--confirm" @click="submit()"></div>
      <div class="popup__button popup__button--cancel" @click="close()"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import statics from "@/statics";
import { formatDisplayDate } from "@/helpers/dateFormatters";

const props = defineProps({
  selected: {
    required: false,
    type: [String, Number],
  },
  items: {
    required: false,
    type: Array,
  },
  position: {
    required: false,
    type: String,
  },
  label: {
    required: false,
    type: [String, Array],
  },
  isDate: {
    required: false,
    type: Boolean,
  },
  isPrice: {
    required: false,
    type: Boolean,
  },
  allowNegative: {
    required: false,
    type: Boolean,
  },
});

const emit = defineEmits(["togglePopup", "submit"]);

const value = ref("");
const x = ref(0);
const y = ref(0);

// Template refs
const popup = ref(null);
const vselect = ref(null);
const datepicker = ref(null);
const priceInput = ref(null);
const textInput = ref(null);

const event = (ev) => {
  const { target } = ev;
  const popupEl = popup.value;

  if (
    target === popupEl ||
    target === popupEl.parentElement ||
    [...popupEl.getElementsByTagName("*")].includes(target)
  ) {
    emit("togglePopup", true);
  } else if (
    document.querySelector(".flatpickr-calendar") &&
    [
      ...document
        .querySelector(".flatpickr-calendar")
        .getElementsByTagName("*"),
    ].includes(target)
  ) {
    emit("togglePopup", true);
  } else {
    close();
  }
};

const submit = () => {
  if (props.isPrice && !props.allowNegative && (isNaN(value.value) || value.value <= 0)) {
    alert("Please enter a valid price greater than 0.");
  } else {
    emit("submit", value.value);
    close();
  }
};

const computePopupLocation = () => {
  const popupEl = popup.value;
  x.value =
    popupEl.parentElement.getBoundingClientRect().left +
    popupEl.parentElement.getBoundingClientRect().width +
    10;
  y.value = popupEl.parentElement.getBoundingClientRect().top - 20;

  nextTick(() => {
    const popupRect = popupEl.getBoundingClientRect();

    const isOutside = popupRect.right > window.innerWidth;

    if (isOutside) {
      x.value =
        popupEl.parentElement.getBoundingClientRect().left -
        popupRect.width -
        10;
    }
  });
};

const close = () => {
  emit("togglePopup", false);
};

onMounted(() => {
  computePopupLocation();
  document.addEventListener("click", event);
  if (!props.items) {
    value.value = props.selected;
  }
  if (props.isDate) {
    datepicker.value.$el.value = formatDisplayDate(props.selected);
  }
  nextTick(() => {
    if (props.isPrice) {
      priceInput.value?.focus();
    } else if (props.isDate) {
      //TODO
      //datepicker.value?.$el.focus();
    } else if (props.items && Array.isArray(props.label)) {
      //TODO
      //vselect.value?.focus();
    } else {
      textInput.value?.focus();
    }
  });
});

onUnmounted(() => {
  document.removeEventListener("click", event);
});
</script>

<style scoped lang="scss">
.popup {
  position: fixed;
  z-index: 3;
  width: 320px;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-2);
  background: var(--bg-1);
  box-shadow: 0px 3px 6px var(--shadow-7);
  padding: 10px 20px;
  cursor: default;

  &--left {
    left: initial;
    right: calc(100% + 10px);
  }

  .select {
    min-width: 200px;
    max-width: 200px;
  }

  &__tools {
    display: flex;
    align-items: center;
  }

  &__button {
    min-width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;

    &--confirm {
      background: url("../assets/icons/default/confirm_icon.svg");
      margin: 0 15px;

      &:hover {
        background: url("../assets/icons/default/confirm_hover_icon.svg");
      }
    }

    &--cancel {
      background: url("../assets/icons/default/close_icon.svg");

      &:hover {
        background: url("../assets/icons/default/close_hover_icon.svg");
      }
    }
  }
}
</style>
